import { useEffect, useMemo, useRef } from 'react';

import { useQueries } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import {
  hasBoundSecret,
  type Connector,
  type ConnectorFeature,
} from '@/types/core/connector';
import type { Page } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

const DEBUG_LOADING_LOGS = import.meta.env.DEV;

/**
 * For a list of connectors, fetches each connector's features and determines:
 * - `configuredConnectorIds`: connectors with at least one feature that has a real (non-NIL) bound secret
 * - `enabledConnectorIds`: connectors with at least one feature where `status === true`
 */
export function useConnectorConfigurationStatus(
  tenantId: UUID | undefined,
  connectors: Connector[]
): {
  configuredConnectorIds: Set<string>;
  enabledConnectorIds: Set<string>;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  hasStableSnapshot: boolean;
} {
  const enabled = !!tenantId && connectors.length > 0;
  const connectorSignature = useMemo(
    () => connectors.map((connector) => connector.id).join('|'),
    [connectors]
  );
  const lastKnownStableSnapshotRef = useRef<{
    configuredConnectorIds: Set<string>;
    enabledConnectorIds: Set<string>;
    connectorSignature: string;
    hasStableSnapshot: boolean;
  }>({
    configuredConnectorIds: new Set(),
    enabledConnectorIds: new Set(),
    connectorSignature,
    hasStableSnapshot: false,
  });

  useEffect(() => {
    lastKnownStableSnapshotRef.current = {
      configuredConnectorIds: new Set(),
      enabledConnectorIds: new Set(),
      connectorSignature,
      hasStableSnapshot: false,
    };
  }, [connectorSignature, tenantId]);

  const fetchConnectorFeatures = async (
    connectorId: UUID
  ): Promise<Page<ConnectorFeature>> => {
    if (!tenantId) {
      throw new Error('Missing tenantId');
    }

    const page = await connectorService.listConnectorFeatures(
      tenantId,
      connectorId,
      {
        limit: 100,
      }
    );

    if (page.items.length === 0) {
      throw new Error('Empty features response, retrying');
    }

    return page;
  };

  const results = useQueries({
    queries: connectors.map((c) => ({
      queryKey: connectorQueryKeys.configurationStatus(tenantId, c.id),
      queryFn: () => fetchConnectorFeatures(c.id),
      enabled,
      retry: 3,
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const isFetching = results.some((r) => r.isFetching);
  const isError = results.some((r) => r.isError);
  const lastQuerySnapshotRef = useRef('');

  const computedSnapshot = useMemo(() => {
    const configured = new Set<string>();
    const enabledSet = new Set<string>();
    const hasCompleteSnapshot = results.every((result) => result?.data != null);
    for (let i = 0; i < connectors.length; i++) {
      const query = results[i];
      if (query?.data) {
        const features = query.data.items ?? [];
        const hasSecret = features.some((f) => hasBoundSecret(f));
        if (hasSecret) configured.add(connectors[i].id);
        const hasEnabled = features.some(
          (f) => f.status === true && f.schedulable !== false
        );
        if (hasEnabled) enabledSet.add(connectors[i].id);
      }
    }
    return {
      configuredConnectorIds: configured,
      enabledConnectorIds: enabledSet,
      hasCompleteSnapshot,
    };
  }, [connectors, results]);

  if (computedSnapshot.hasCompleteSnapshot) {
    lastKnownStableSnapshotRef.current = {
      configuredConnectorIds: computedSnapshot.configuredConnectorIds,
      enabledConnectorIds: computedSnapshot.enabledConnectorIds,
      connectorSignature,
      hasStableSnapshot: true,
    };
  }

  const hasStableSnapshot =
    lastKnownStableSnapshotRef.current.hasStableSnapshot &&
    lastKnownStableSnapshotRef.current.connectorSignature ===
      connectorSignature;

  const configuredConnectorIds = hasStableSnapshot
    ? lastKnownStableSnapshotRef.current.configuredConnectorIds
    : computedSnapshot.configuredConnectorIds;
  const enabledConnectorIds = hasStableSnapshot
    ? lastKnownStableSnapshotRef.current.enabledConnectorIds
    : computedSnapshot.enabledConnectorIds;

  const querySnapshot = useMemo(
    () =>
      results
        .map((result, index) => {
          const connectorId = connectors[index]?.id ?? 'missing';
          return [
            connectorId,
            result?.status ?? 'missing',
            result?.fetchStatus ?? 'missing',
            result?.isLoading ? 'loading' : 'idle',
            result?.isFetching ? 'fetching' : 'steady',
            result?.isError ? 'error' : 'ok',
            result?.data?.items?.length ?? 0,
          ].join(':');
        })
        .join('|'),
    [connectors, results]
  );

  useEffect(() => {
    if (!DEBUG_LOADING_LOGS || !enabled) {
      return;
    }

    if (querySnapshot === lastQuerySnapshotRef.current) {
      return;
    }

    lastQuerySnapshotRef.current = querySnapshot;

    console.info('[useConnectorConfigurationStatus] state', {
      tenantId,
      connectorCount: connectors.length,
      isLoading,
      isFetching,
      isError,
      hasStableSnapshot,
      configuredConnectorCount: configuredConnectorIds.size,
      enabledConnectorCount: enabledConnectorIds.size,
      queries: results.map((result, index) => ({
        connectorId: connectors[index]?.id,
        status: result?.status,
        fetchStatus: result?.fetchStatus,
        isLoading: result?.isLoading,
        isFetching: result?.isFetching,
        isError: result?.isError,
        itemCount: result?.data?.items?.length ?? 0,
        error: result?.error?.message,
      })),
    });
  }, [
    connectors,
    enabled,
    enabledConnectorIds.size,
    configuredConnectorIds.size,
    isFetching,
    isError,
    isLoading,
    hasStableSnapshot,
    querySnapshot,
    results,
    tenantId,
  ]);

  return {
    configuredConnectorIds,
    enabledConnectorIds,
    isLoading,
    isFetching,
    isError,
    hasStableSnapshot,
  };
}
